import { createBrowserRouter, Navigate, redirect } from "react-router-dom";
import { AdminLayout } from "@/admin/AdminLayout";
import { DashboardPage } from "@/admin/DashboardPage";
import { VpnProfilesPage } from "@/admin/VpnProfilesPage";
import { TenantsPage } from "@/admin/TenantsPage";
import { ServersPage } from "@/admin/ServersPage";
import { AuditPage } from "@/admin/AuditPage";
import { SettingsPage } from "@/admin/SettingsPage";
import { PortalHome } from "@/portal/PortalHome";
import { AuthCallback } from "@/auth/AuthCallback";
import { RequireSession } from "@/auth/RequireSession";
import { beginLogin, getCurrentPrincipal, isAuthenticated } from "@/api/client";

/**
 * Путь, на который вернуть человека после входа, — из `request` лоадера, а не
 * из `window.location`: при клиентской навигации react-router обновляет адрес
 * только после успешного завершения лоадера, так что `window.location` там
 * ещё старый. Хардкод `/admin` терял глубокую ссылку целиком.
 */
function returnPathOf(request: Request): string {
  const u = new URL(request.url);
  return `${u.pathname}${u.search}`;
}

// Нет токена — начинаем вход и отдаём рендер элементу: `RequireSession`
// покажет «Входим…». Бросать `Response` 302 без `Location` нельзя — это
// дефолтный экран ошибки роутера (`errorElement` в дереве нет). Раньше он лишь
// мелькал до редиректа браузера, но с 0.12 вход из скрытой страницы
// откладывается до её показа — экран висел бы всё это время.
function requireAuth() {
  return ({ request }: { request: Request }) => {
    if (!isAuthenticated()) beginLogin(returnPathOf(request));
    return null;
  };
}

// Роли с доступом к admin-консоли. Зеркало backend
// NET_ADMIN_ROLES = ("admin", "tenant_owner"). Держать синхронно.
const ADMIN_ROLES = ["admin", "tenant_owner"];

function requireAdminRole() {
  return ({ request }: { request: Request }) => {
    if (!isAuthenticated()) {
      beginLogin(returnPathOf(request));
      return null;
    }
    const p = getCurrentPrincipal();
    // Default-deny: в /admin пускаем ТОЛЬКО admin / tenant_owner. Все
    // остальные (net_role === "user", отсутствие net-роли — null) уходят
    // в личный кабинет. Иначе backend всё равно ответит 403 на /v1/admin/*,
    // а фронт покажет это как «сотрудник не нашёлся».
    //
    // Это НЕ путь логина: `redirect()` отдаёт Response с `Location`, его
    // отрабатывает сам роутер, и с auth такой переход закольцеваться не может.
    if (!p || !ADMIN_ROLES.includes(p.net_role ?? "")) {
      throw redirect("/my");
    }
    return null;
  };
}

export const router = createBrowserRouter([
  // Точка возврата из signaris-auth /api/sso/authorize. Принимает
  // одноразовый `?code=...` (Authorization Code + PKCE), меняет на токены.
  { path: "/auth/callback", element: <AuthCallback /> },

  // Корень — редиректим в /admin (admin-роли) или /my (user). Решает
  // requireAdminRole loader: если user, бросает redirect в /my.
  { path: "/", element: <Navigate to="/admin" replace /> },

  // Admin
  {
    path: "/admin",
    element: (
      <RequireSession>
        <AdminLayout />
      </RequireSession>
    ),
    loader: requireAdminRole(),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "vpn-profiles", element: <VpnProfilesPage /> },
      // Алиас на старый URL — на случай ссылок в почте/закладках.
      {
        path: "clients",
        element: <Navigate to="/admin/vpn-profiles" replace />,
      },
      { path: "servers", element: <ServersPage /> },
      { path: "tenants", element: <TenantsPage /> },
      { path: "audit", element: <AuditPage /> },
      { path: "settings", element: <SettingsPage /> },
    ],
  },

  // User Portal — для любой роли NET (user / tenant_*)
  {
    path: "/my",
    element: (
      <RequireSession>
        <PortalHome />
      </RequireSession>
    ),
    loader: requireAuth(),
  },
]);
